import React, { useState, useEffect } from 'react';
import { FovModal, FovSelect, FovOption, FovToggle, VerticalSpacer } from 'components';
import {
  ModelSelectWrapper,
  SimpleFieldLabel,
  SimpleFieldInput,
  SimpleToggleRow,
  SimpleToggleList,
  GenerateButton,
} from '../imageCanvasStyles';
import {
  RODIN_QUALITIES,
  RODIN_TIERS,
  RODIN_MATERIALS,
  RODIN_MESH_MODES,
  RODIN_CONDITION_MODES,
  RODIN_QUALITY_OVERRIDE_MIN,
  RODIN_QUALITY_OVERRIDE_MAX,
  RODIN_SEED_MIN,
  RODIN_SEED_MAX,
  validateRodinSettings,
} from '../rodinSettings';

const QUALITY_LABELS = {
  high: 'High (50k)',
  medium: 'Medium (18k)',
  low: 'Low (8k)',
  'extra-low': 'Extra-low (4k)',
};

const clampInRange = (value, min, max, integer) => {
  if (value === '' || value === null || value === undefined) return '';
  const numeric = Number(value) || min;
  const bounded = Math.min(max, Math.max(min, numeric));
  return integer ? Math.round(bounded) : bounded;
};

const RodinSettingsModal = ({ theme, selectedTheme, visible, settings, onCancel, onSave }) => {
  const shared = { theme, $selectedTheme: selectedTheme };
  const [ draft, setDraft ] = useState(settings);

  useEffect(() => {
    if (visible) setDraft(settings);
  }, [ visible, settings ]);

  const handleSave = () => {
    const { valid } = validateRodinSettings(draft);
    if (!valid) return;
    onSave(draft);
  };

  const isRaw = draft.meshMode === 'Raw';
  const isQuad = draft.meshMode === 'Quad';

  return (
    <FovModal title='Rodin AI Settings' visible={visible} onClose={onCancel} width={360}>
      <SimpleFieldLabel {...shared}>Quality</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.quality}
          placeholder={QUALITY_LABELS[draft.quality]}
          onChange={(e, newVal) => setDraft({ ...draft, quality: newVal })}
        >
          {RODIN_QUALITIES.map(value => (
            <FovOption key={value} value={value}>{QUALITY_LABELS[value]}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Tier</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.tier}
          placeholder={draft.tier}
          onChange={(e, newVal) => setDraft({ ...draft, tier: newVal })}
        >
          {RODIN_TIERS.map(value => (
            <FovOption key={value} value={value}>{value}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Material</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.material}
          placeholder={draft.material}
          onChange={(e, newVal) => setDraft({ ...draft, material: newVal })}
        >
          {RODIN_MATERIALS.map(value => (
            <FovOption key={value} value={value}>{value}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Mesh Mode</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.meshMode}
          placeholder={draft.meshMode}
          onChange={(e, newVal) => setDraft({ ...draft, meshMode: newVal })}
        >
          {RODIN_MESH_MODES.map(value => (
            <FovOption key={value} value={value}>{value}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Condition Mode (multi-image)</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.conditionMode}
          placeholder={draft.conditionMode}
          onChange={(e, newVal) => setDraft({ ...draft, conditionMode: newVal })}
        >
          {RODIN_CONDITION_MODES.map(value => (
            <FovOption key={value} value={value}>{value}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Quality Override (blank = preset)</SimpleFieldLabel>
      <SimpleFieldInput
        {...shared}
        type='number'
        min={RODIN_QUALITY_OVERRIDE_MIN}
        max={RODIN_QUALITY_OVERRIDE_MAX}
        placeholder='Use quality preset'
        value={draft.qualityOverride}
        onChange={e => setDraft({ ...draft, qualityOverride: e.target.value })}
        onBlur={() => setDraft({
          ...draft,
          qualityOverride: clampInRange(
            draft.qualityOverride, RODIN_QUALITY_OVERRIDE_MIN, RODIN_QUALITY_OVERRIDE_MAX, true,
          ),
        })}
      />

      <SimpleFieldLabel {...shared}>Seed (blank = random)</SimpleFieldLabel>
      <SimpleFieldInput
        {...shared}
        type='number'
        min={RODIN_SEED_MIN}
        max={RODIN_SEED_MAX}
        placeholder='Random'
        value={draft.seed}
        onChange={e => setDraft({ ...draft, seed: e.target.value })}
        onBlur={() => setDraft({
          ...draft,
          seed: clampInRange(draft.seed, RODIN_SEED_MIN, RODIN_SEED_MAX, true),
        })}
      />

      <SimpleFieldLabel {...shared}>Prompt (blank = none)</SimpleFieldLabel>
      <SimpleFieldInput
        {...shared}
        type='text'
        placeholder='Optional guiding prompt'
        value={draft.prompt}
        onChange={e => setDraft({ ...draft, prompt: e.target.value })}
      />

      <VerticalSpacer size={15} />

      <SimpleToggleList>
        <SimpleToggleRow {...shared}>
          Mesh Simplify

          <FovToggle
            name='meshSimplifyToggle'
            checked={!!draft.meshSimplify}
            disabled={!isRaw}
            callback={value => setDraft({ ...draft, meshSimplify: value })}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          Mesh Smooth

          <FovToggle
            name='meshSmoothToggle'
            checked={!!draft.meshSmooth}
            disabled={!isQuad}
            callback={value => setDraft({ ...draft, meshSmooth: value })}
          />
        </SimpleToggleRow>
      </SimpleToggleList>

      <VerticalSpacer size={20} />

      <GenerateButton {...shared} onClick={handleSave}>
        Save
      </GenerateButton>
    </FovModal>
  );
};

export { RodinSettingsModal };
